// synced from loomweb blocks/remote-command-block-view.tsx (de-Tailwind'd).
/**
 * Remote command-execution block (RemoteExecute). Faithful to loomweb's
 * RemoteCommandBlockView in intent: a header (Monitor icon + a host identity
 * `user@host` / label / command / job-id, a running / done / failed state, a copy
 * button), the executed command, and a collapsible output region. Output prefers
 * the time-ordered `outputLines`; otherwise it falls back to `stdout` then
 * `stderr`. loomweb's auto-collapse timers are dropped — the replay is static, so
 * collapse is driven once by a simple line-count heuristic (long output starts
 * collapsed, errors / short output start expanded). Tailwind classes are replaced
 * by namespaced `replay-*` CSS; lucide icons by the local inline-SVG set.
 */
import type { JSX } from "react";
import { useMemo, useState } from "react";
import type { RemoteCommandBlock } from "@shared/loom-message-block";
import {
  MonitorIcon,
  LoaderIcon,
  CheckIcon,
  XIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from "./icons";
import { CopyButton } from "./copy-button";

/** Output collapse threshold (lines): more than this starts collapsed. */
const COLLAPSE_THRESHOLD = 5;

export function RemoteCommandBlockView({ block }: { block: RemoteCommandBlock }): JSX.Element {
  const isRunning = block.status === "in_progress" || block.status === "streaming";
  const isCompleted = block.status === "completed";
  const isError = block.status === "error";
  const hasFailed = block.exitCode !== undefined && block.exitCode !== 0;

  // Total output lines, preferring the time-ordered outputLines.
  const totalLines =
    block.outputLines?.length ??
    (block.output?.stdout.length ?? 0) + (block.output?.stderr.length ?? 0);

  // Initial output collapse: expanded while running / on error / for short output;
  // otherwise honour an explicit `collapsed` flag, else collapse long output.
  const initialCollapsed = useMemo<boolean>(() => {
    if (isRunning || isError || hasFailed) return false;
    if (totalLines <= COLLAPSE_THRESHOLD) return false;
    if (block.collapsed !== undefined) return block.collapsed;
    return true;
  }, [isRunning, isError, hasFailed, totalLines, block.collapsed]);

  const [collapsed, setCollapsed] = useState<boolean>(initialCollapsed);

  // Header text: prefer user@host, then label, then the command, then the job id.
  const headerText = useMemo<string>(() => {
    if (block.host !== undefined && block.username !== undefined) {
      return `${block.username}@${block.host}`;
    }
    if (block.label !== undefined && block.label.length > 0) return block.label;
    if (block.command !== undefined && block.command.length > 0) return block.command;
    return `任务 ${block.jobId.slice(0, 8)}...`;
  }, [block.host, block.username, block.label, block.command, block.jobId]);

  const hasHostIdentity =
    (block.host !== undefined && block.username !== undefined) ||
    (block.label !== undefined && block.label.length > 0);
  const showCommandRow =
    hasHostIdentity && block.command !== undefined && block.command.length > 0;
  const showCollapseToggle = totalLines > COLLAPSE_THRESHOLD;

  return (
    <div className="replay-block replay-remote-command-block">
      <div className="replay-block-head">
        <MonitorIcon className="replay-icon replay-text-cyan" />
        <span className="replay-remote-header">{headerText}</span>
        {isRunning && (
          <span className="replay-block-flag replay-text-primary">
            <LoaderIcon className="replay-icon-xs replay-spin" />
          </span>
        )}
        {isCompleted && !hasFailed && (
          <span className="replay-block-flag replay-text-success">
            <CheckIcon className="replay-icon-xs" />
          </span>
        )}
        {(isError || hasFailed) && (
          <span className="replay-block-flag replay-text-error">
            <XIcon className="replay-icon-xs" />
          </span>
        )}
        {block.command !== undefined && block.command.length > 0 && (
          <CopyButton text={block.command} className="replay-ml-auto" ariaLabel="复制命令" />
        )}
      </div>

      {showCommandRow && (
        <div className="replay-remote-command-row replay-mono">{block.command}</div>
      )}

      {isRunning && block.output === undefined && (
        <div className="replay-remote-running">
          <LoaderIcon className="replay-icon-xs replay-spin" />
          <span>正在执行...</span>
        </div>
      )}

      {block.output !== undefined && (
        <div className="replay-remote-output-wrap">
          {showCollapseToggle && (
            <button
              type="button"
              className="replay-trp-toggle"
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((v) => !v)}
            >
              {collapsed ? (
                <ChevronRightIcon className="replay-icon-xs" />
              ) : (
                <ChevronDownIcon className="replay-icon-xs" />
              )}
              <span>{collapsed ? `展开输出 (${totalLines} 行)` : `收起输出 (${totalLines} 行)`}</span>
            </button>
          )}

          {(!showCollapseToggle || !collapsed) && (
            <div className="replay-remote-output replay-mono" role="region" aria-label="命令输出">
              {block.outputLines !== undefined
                ? block.outputLines.map((line, i) => (
                    <div
                      key={`line-${i}`}
                      className={
                        line.type === "progress"
                          ? "replay-remote-line replay-remote-line-progress"
                          : "replay-remote-line"
                      }
                    >
                      {line.content}
                    </div>
                  ))
                : [
                    ...block.output.stdout.map((line, i) => (
                      <div key={`out-${i}`} className="replay-remote-line">
                        {line}
                      </div>
                    )),
                    ...block.output.stderr.map((line, i) => (
                      <div key={`err-${i}`} className="replay-remote-line">
                        {line}
                      </div>
                    ))
                  ]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
